import sumBy from 'lodash/sumBy';
import * as React from 'react';
import type { OnlinePlannerSubject } from '../../../Interfaces/OnlinePlannerSubject';
import type { PurchasableRestrictedSingleSubject } from '../../../Interfaces/RestrictedSingleSubject';
import { checkIfSubjectIsSelected } from '../utils';
import { groupSubjects } from './getGroupedSubjects';
import { plannerReducer } from './plannerReducer';
import type {
    ErrorTypes,
    PlannerAction,
    PlannerBookmark,
    PlannerContext,
    PlannerContextProviderProps,
    PlannerFilterType,
    PlannerHelpers,
    PlannerProjectedState,
    PlannerSelectedSubjects,
    PlannerSetters,
    PlannerState,
    PlannerSubjectsGroupingType,
    SemesterNumber,
} from './types';

const Context = React.createContext<PlannerContext | null>(null);

const defaultState: PlannerState = {
    internalSelectedSubjects: new Map(),
    currentBookmark: { uniqueId: -1, label: '', contextKey: 'partTime' },
    currentFilter: 'all',
    isPopupVisible: false,
    isApplicationProcessFailed: false,
};

const BuildPlannerSetters = (dispatch: React.Dispatch<PlannerAction>): PlannerSetters => {
    const setCurrentBookmark = (bookmark: PlannerBookmark) => dispatch({ type: 'SET_CURRENT_BOOKMARK', bookmark });
    const setCurrentFilter = (filter: PlannerFilterType) => dispatch({ type: 'SET_CURRENT_FILTER', filter });
    const addSelectedSubject = (subject: PurchasableRestrictedSingleSubject, semester: SemesterNumber) =>
        dispatch({ type: 'ADD_SELECTED_SUBJECT', subject, semester });
    const addBatchSelectedSubjects = (subjects: PurchasableRestrictedSingleSubject[], semester: SemesterNumber) =>
        dispatch({ type: 'ADD_BATCH_SELECTED_SUBJECTS', subjects, semester });
    const removeSelectedSubject = (subject: PurchasableRestrictedSingleSubject) =>
        dispatch({ type: 'REMOVE_SELECTED_SUBJECT', subject });
    const removeBatchSelectedSubjects = (semester: SemesterNumber) =>
        dispatch({ type: 'REMOVE_BATCH_SELECTED_SUBJECTS', semester });
    const removeAllSelectedSubjects = () => dispatch({ type: 'REMOVE_ALL_SELECTED_SUBJECTS' });
    const setPopupVisibility = (open: boolean) => dispatch({ type: 'SET_POPUP_VISIBILITY', open });
    const setError = (errorType: ErrorTypes, errorMessage?: string) =>
        dispatch({ type: 'SET_ERROR', errorType, errorMessage });

    return {
        setCurrentBookmark,
        setCurrentFilter,
        addSelectedSubject,
        addBatchSelectedSubjects,
        setPopupVisibility,
        removeSelectedSubject,
        removeBatchSelectedSubjects,
        removeAllSelectedSubjects,
        setError,
    };
};

const BuildProjectedState = (
    subjects: OnlinePlannerSubject[],
    selectedSubjects: PlannerSelectedSubjects,
    filterOptions: Map<PlannerFilterType, string>,
    currentBookmark: PlannerBookmark,
    currentFilter: PlannerFilterType
): PlannerProjectedState => {
    const allSelectedSubjects = React.useMemo(() => Array.from(selectedSubjects.values()), [selectedSubjects]);

    const selectedElectiveSubjects = React.useMemo(
        () => allSelectedSubjects.filter(subject => subject.electiveGroupCode),
        [allSelectedSubjects]
    );

    const totalPrice = React.useMemo(
        () =>
            allSelectedSubjects.length === 0
                ? { amount: 0, currencyCode: '', currencySymbol: '' }
                : {
                      amount: sumBy(allSelectedSubjects, subject => subject.price.amount),
                      currencyCode: allSelectedSubjects[0].price.currencyCode,
                      currencySymbol: allSelectedSubjects[0].price.currencySymbol,
                  },
        [allSelectedSubjects]
    );

    const totalPoints = React.useMemo(
        () => sumBy(allSelectedSubjects, subject => subject.points.amount),
        [allSelectedSubjects]
    );

    const groupedSubjects = React.useMemo(
        () => groupSubjects(subjects, filterOptions, currentBookmark.contextKey),
        [subjects, currentBookmark.contextKey]
    );

    const filteredSubjects = React.useMemo(() => {
        const subjectsMatchingFilter = groupedSubjects.get(currentFilter);

        return subjectsMatchingFilter ? new Map([[currentFilter, subjectsMatchingFilter]]) : undefined;
    }, [groupedSubjects, currentFilter]);

    return {
        allSelectedSubjects,
        selectedElectiveSubjects,
        totalPrice,
        totalPoints,
        groupedSubjects,
        filteredSubjects,
    };
};

const BuildPlannerHelpers = (
    selectedSubjects: PlannerSelectedSubjects,
    activeTab: PlannerSubjectsGroupingType
): PlannerHelpers => {
    const isSubjectSelected = (subject: PurchasableRestrictedSingleSubject, semester: SemesterNumber) =>
        checkIfSubjectIsSelected(subject, activeTab, semester, selectedSubjects);

    const isSubjectDisabled = (subject: PurchasableRestrictedSingleSubject, semester: SemesterNumber) => {
        if (isSubjectSelected(subject, semester)) {
            return false;
        }

        const selectedSubject = selectedSubjects.get(subject.subjectCode);

        if (!selectedSubject || selectedSubject.tab !== activeTab) {
            return false;
        }

        return selectedSubject.semester !== semester;
    };

    return {
        isSubjectDisabled,
        isSubjectSelected,
    };
};

export const PlannerContextProvider: React.FC<React.PropsWithChildren<PlannerContextProviderProps>> = props => {
    const { children, subjects, filterOptions, bookmarks, ...restStaticData } = props;

    const [{ internalSelectedSubjects, currentBookmark, currentFilter, ...restState }, dispatch] = React.useReducer(
        plannerReducer,
        {
            ...defaultState,
            ...{ currentBookmark: bookmarks[0] },
        }
    );

    const context: PlannerContext = {
        currentBookmark,
        currentFilter,
        filterOptions,
        bookmarks,
        ...restStaticData,
        ...restState,
        ...BuildPlannerSetters(dispatch),
        ...BuildProjectedState(subjects, internalSelectedSubjects, filterOptions, currentBookmark, currentFilter),
        ...BuildPlannerHelpers(internalSelectedSubjects, currentBookmark.contextKey),
    };

    return <Context.Provider value={context}>{children}</Context.Provider>;
};

export const usePlannerContext = () => {
    const context = React.useContext(Context);

    if (!context) {
        throw new Error('Planer Context has not been initialized');
    }

    return context;
};
